import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import {
  listEmailCampaigns,
  updateEmailCampaign,
  getEmailCampaignRecipientCount,
  sendEmailCampaignTestNow,
  resetEmailCampaign,
  getAdminEmailMetrics,
  type EmailCampaign,
  type EmailCampaignUpdate,
  type EpisodeReminderWeekMetrics,
} from '../../lib/api/admin';
import { Modal } from './Modal';
import { isoToLocalInput, localInputToIso } from '../../lib/datetimeLocal';

// Edits use native datetime-local inputs in the editor's LOCAL timezone; every
// field shows an America/New_York (event timezone) preview as the source-of-
// truth check, matching the Schedule tab. Send counts come from the API's own
// per-recipient ledger — SES delivery/open/bounce metrics live on Overview.

const DISPLAY_TZ = 'America/New_York';

function fmtEt(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: DISPLAY_TZ,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return new Date(iso).toUTCString();
  }
}

const previewEt = (local: string): string => fmtEt(localInputToIso(local));

function parseRecipients(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\n,]/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
}

interface Draft {
  enabled: boolean;
  scheduled: string; // datetime-local string
  subject: string;
  episodeNumber: string;
  title: string;
  ctaPath: string;
  recipients: string; // test entry only — newline/comma separated
}

function toDraft(c: EmailCampaign): Draft {
  return {
    enabled: c.enabled,
    scheduled: isoToLocalInput(c.scheduledSendAt),
    subject: c.subject,
    episodeNumber: c.contentVars.episodeNumber != null ? String(c.contentVars.episodeNumber) : '',
    title: c.contentVars.title != null ? String(c.contentVars.title) : '',
    ctaPath: c.contentVars.ctaPath != null ? String(c.contentVars.ctaPath) : '/play',
    recipients: c.testRecipients.join('\n'),
  };
}

function buildUpdate(draft: Draft, c: EmailCampaign): EmailCampaignUpdate {
  const update: EmailCampaignUpdate = {};
  if (draft.enabled !== c.enabled) update.enabled = draft.enabled;

  const schedIso = localInputToIso(draft.scheduled);
  if (schedIso !== c.scheduledSendAt) update.scheduledSendAt = schedIso;

  if (draft.subject !== c.subject) update.subject = draft.subject;

  const newVars = {
    ...c.contentVars,
    episodeNumber: draft.episodeNumber.trim() === '' ? 0 : Number(draft.episodeNumber),
    title: draft.title,
    ctaPath: draft.ctaPath.trim() || '/play',
  };
  if (JSON.stringify(newVars) !== JSON.stringify(c.contentVars)) update.contentVars = newVars;

  if (c.kind === 'test') {
    const parsed = parseRecipients(draft.recipients);
    if (JSON.stringify(parsed) !== JSON.stringify(c.testRecipients)) update.testRecipients = parsed;
  }
  return update;
}

const STATE_STYLES: Record<EmailCampaign['sendState'], string> = {
  IDLE: 'bg-white/10 text-white/70',
  CLAIMED: 'bg-amber-400/20 text-amber-200',
  SENDING: 'bg-amber-400/20 text-amber-200',
  SENT: 'bg-emerald-400/20 text-emerald-200',
  FAILED: 'bg-rose-500/20 text-rose-200',
  MISSED: 'bg-rose-500/20 text-rose-200',
};

/**
 * Email Sender tab — manage the fixed set of episode-reminder campaigns (1 test
 * + 5 weekly). Per row: enable/disable, send date/time, subject + content,
 * status + last-run counts. Enabling a real send confirms the live recipient
 * count first; the test row can be fired on demand.
 */
const EMAIL_METRICS_WINDOW_DAYS = 30;

// 'loading' until the CloudWatch read resolves; the episode-reminder type (or
// null if CloudWatch was unavailable) once ready.
type ReminderMetricsState =
  | { kind: 'loading' }
  | { kind: 'ready'; weeks: EpisodeReminderWeekMetrics[] | null };

export function EmailSenderTab() {
  const [campaigns, setCampaigns] = useState<EmailCampaign[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<ReminderMetricsState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    listEmailCampaigns({ signal: controller.signal })
      .then((c) => setCampaigns(c))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setLoadError(
          err instanceof ApiError ? `Couldn't load campaigns (${err.status}).` : "Couldn't load campaigns.",
        );
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    getAdminEmailMetrics({ window: EMAIL_METRICS_WINDOW_DAYS }, { signal: controller.signal })
      .then((data) => {
        setMetrics({ kind: 'ready', weeks: data.metricsAvailable ? data.episodeReminderWeeks : null });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setMetrics({ kind: 'ready', weeks: null }); // soft-fail; panel shows "unavailable"
      });
    return () => controller.abort();
  }, []);

  const onChanged = (updated: EmailCampaign): void => {
    setCampaigns((cs) => (cs ? cs.map((c) => (c.campaignKey === updated.campaignKey ? updated : c)) : cs));
  };

  if (loadError) return <p className="text-white/80">{loadError}</p>;
  if (!campaigns) return <p className="text-white/60">Loading campaigns…</p>;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-xs text-[#ffae5b]">
        Real sends email every active, opted-in user. Enabling a real send is confirmed with a live recipient
        count first. The test entry sends only to its explicit recipient list. Times are shown in {DISPLAY_TZ}.
      </p>
      <MarketingEngagementPanel state={metrics} />
      {campaigns.map((c) => (
        <CampaignCard key={c.campaignKey} campaign={c} onChanged={onChanged} />
      ))}
    </div>
  );
}

// Per-week SES engagement for the reminder campaigns, read from CloudWatch —
// each send is tagged episode-reminder-<campaignKey>, so rows break out by week
// (week-1..5 + the test send) through the funnel to clicks.
function MarketingEngagementPanel({ state }: { state: ReminderMetricsState }) {
  const weeks = state.kind === 'ready' ? state.weeks : null;
  const cols = [
    'Campaign', 'Sent', 'Delivered', 'Opened', 'Open rate', 'Clicked', 'Click rate', 'Bounced', 'Bounce rate',
  ];
  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-heading text-sm uppercase tracking-wide text-white/70">Per-week engagement</h3>
        <span className="text-[11px] text-white/40">last {EMAIL_METRICS_WINDOW_DAYS} days · CloudWatch</span>
      </div>
      {state.kind === 'loading' ? (
        <p className="mt-4 text-sm text-white/50">Loading email metrics…</p>
      ) : !weeks ? (
        <p className="mt-4 text-sm text-white/50">Email metrics are temporarily unavailable.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/55">
                {cols.map((c) => (
                  <th key={c} className="px-3 py-2 font-heading text-xs uppercase tracking-wide">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.campaignKey} className="border-b border-white/5 last:border-0 text-white/85">
                  <td className="px-3 py-2 text-white/70">{w.label}</td>
                  <td className="px-3 py-2">{fmtNum(w.sent)}</td>
                  <td className="px-3 py-2">{fmtNum(w.delivered)}</td>
                  <td className="px-3 py-2">{fmtNum(w.opened)}</td>
                  <td className="px-3 py-2">{pctStr(w.openRate)}</td>
                  <td className="px-3 py-2 text-emerald-200">{fmtNum(w.clicked)}</td>
                  <td className="px-3 py-2 text-emerald-200">{pctStr(w.clickRate)}</td>
                  <td className="px-3 py-2">{fmtNum(w.bounced)}</td>
                  <td className="px-3 py-2">{pctStr(w.bounceRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-[11px] text-white/40">
        From CloudWatch (SES event metrics), tagged per campaign. Opens are approximate (image-proxy inflation).
      </p>
    </section>
  );
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-US');
}
function pctStr(r: number): string {
  return `${(r * 100).toFixed(1)}%`;
}

function CampaignCard({
  campaign,
  onChanged,
}: {
  campaign: EmailCampaign;
  onChanged: (updated: EmailCampaign) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(campaign));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ count: number | null } | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [testing, setTesting] = useState(false);

  // Reset the local draft if the parent hands down a fresh campaign (e.g. after
  // a send-now / reset).
  useEffect(() => {
    setDraft(toDraft(campaign));
  }, [campaign]);

  const dirty = useMemo(
    () => JSON.stringify(buildUpdate(draft, campaign)) !== '{}',
    [draft, campaign],
  );

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    setSuccess(null);
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const validateForEnable = (): string | null => {
    if (!draft.enabled) return null;
    if (!draft.subject.trim()) return 'A subject is required to enable a send.';
    if (!draft.scheduled) return 'A scheduled send time is required to enable a send.';
    const when = localInputToIso(draft.scheduled);
    if (when && new Date(when).getTime() < Date.now() - 60_000) {
      return 'Scheduled send time is in the past — pick a future time before enabling.';
    }
    return null;
  };

  const persist = async (update: EmailCampaignUpdate): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateEmailCampaign(campaign.campaignKey, update);
      onChanged(updated);
      setSuccess('Saved.');
      setConfirm(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save. Please try again.');
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const onSaveClick = async (): Promise<void> => {
    const validationError = validateForEnable();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    const update = buildUpdate(draft, campaign);
    // Turning a REAL send on → confirm with a live recipient count first.
    const enablingReal = update.enabled === true && campaign.kind === 'real';
    if (enablingReal) {
      setConfirm({ count: null });
      try {
        const count = await getEmailCampaignRecipientCount(campaign.campaignKey);
        setConfirm({ count });
      } catch {
        setConfirm({ count: -1 }); // unknown — still let the admin proceed
      }
      return;
    }
    await persist(update);
  };

  const onSendTest = async (): Promise<void> => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await sendEmailCampaignTestNow();
      onChanged(updated);
      setSuccess(`Test sent — ${updated.sentCount} sent, ${updated.skippedCount} skipped, ${updated.failedCount} failed.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the test. Please try again.');
    } finally {
      setTesting(false);
    }
  };

  const onReset = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const updated = await resetEmailCampaign(campaign.campaignKey);
      onChanged(updated);
      setResetOpen(false);
      setSuccess('Campaign reset to idle.');
    } catch (err) {
      setResetOpen(false);
      setError(err instanceof ApiError ? err.message : 'Could not reset. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inFlight = campaign.sendState === 'SENDING' || campaign.sendState === 'CLAIMED';
  const canReset =
    campaign.sendState === 'SENT' || campaign.sendState === 'FAILED' || campaign.sendState === 'MISSED';

  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="font-heading text-base uppercase tracking-wide text-white">{campaign.label}</h3>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATE_STYLES[campaign.sendState]}`}>
            {campaign.sendState}
          </span>
          {campaign.kind === 'test' && (
            <span className="rounded-full bg-sky-400/15 px-2.5 py-0.5 text-[11px] text-sky-200">test</span>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-white/80">
          <input
            type="checkbox"
            checked={draft.enabled}
            disabled={inFlight}
            onChange={(e) => set('enabled', e.target.checked)}
            className="h-4 w-4 accent-emerald-400"
          />
          Enabled
        </label>
      </header>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          Send date / time
          <input
            type="datetime-local"
            value={draft.scheduled}
            disabled={inFlight}
            onChange={(e) => set('scheduled', e.target.value)}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none [color-scheme:dark]"
          />
          <span className="text-[11px] normal-case tracking-normal text-white/45">
            {draft.scheduled ? previewEt(draft.scheduled) : '—'}
          </span>
        </label>

        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          Subject
          <input
            type="text"
            value={draft.subject}
            disabled={inFlight}
            onChange={(e) => set('subject', e.target.value)}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          Episode number
          <input
            type="number"
            min={0}
            value={draft.episodeNumber}
            disabled={inFlight}
            onChange={(e) => set('episodeNumber', e.target.value)}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          Episode title
          <input
            type="text"
            value={draft.title}
            disabled={inFlight}
            onChange={(e) => set('title', e.target.value)}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          CTA path
          <input
            type="text"
            value={draft.ctaPath}
            disabled={inFlight}
            onChange={(e) => set('ctaPath', e.target.value)}
            placeholder="/play"
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none"
          />
        </label>

        {campaign.kind === 'test' && (
          <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55 sm:col-span-2">
            Test recipients (one per line or comma-separated)
            <textarea
              value={draft.recipients}
              disabled={inFlight}
              rows={3}
              onChange={(e) => set('recipients', e.target.value)}
              placeholder="dev@infinitymarketing.com"
              className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white focus:border-white/40 focus:outline-none"
            />
            <span className="text-[11px] normal-case tracking-normal text-white/45">
              Falls back to EMAIL_TEST_RECIPIENTS if left empty.
            </span>
          </label>
        )}
      </div>

      {/* Last-run summary (from the ledger). */}
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-white/55">
        <span>
          Recipients: <span className="text-white/80">{campaign.totalRecipients}</span>
        </span>
        <span>
          Sent: <span className="text-emerald-200">{campaign.sentCount}</span>
        </span>
        <span>
          Skipped: <span className="text-white/80">{campaign.skippedCount}</span>
        </span>
        <span>
          Failed: <span className={campaign.failedCount ? 'text-rose-200' : 'text-white/80'}>{campaign.failedCount}</span>
        </span>
        {campaign.completedAt && <span>Completed: {fmtEt(campaign.completedAt)}</span>}
      </div>
      {campaign.lastError && <p className="mt-1 text-[12px] text-rose-200">Last error: {campaign.lastError}</p>}

      {error && <p className="mt-3 text-sm text-[#ff8da6]">{error}</p>}
      {success && <p className="mt-3 text-sm text-[#5bffb4]">{success}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void onSaveClick()}
          disabled={!dirty || saving || inFlight}
          className="rounded-full bg-white/90 px-5 py-2 text-sm font-medium text-[#2a2237] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => {
              setDraft(toDraft(campaign));
              setError(null);
              setSuccess(null);
            }}
            disabled={saving}
            className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:opacity-40"
          >
            Discard
          </button>
        )}
        {campaign.kind === 'test' && (
          <button
            type="button"
            onClick={() => void onSendTest()}
            disabled={testing || inFlight}
            className="rounded-full border border-sky-300/40 bg-sky-400/10 px-5 py-2 text-sm text-sky-100 transition hover:bg-sky-400/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {testing ? 'Sending…' : 'Send test now'}
          </button>
        )}
        {canReset && (
          <button
            type="button"
            onClick={() => setResetOpen(true)}
            disabled={saving}
            className="rounded-full border border-white/25 px-5 py-2 text-sm text-white/80 transition hover:bg-white/10 disabled:opacity-40"
          >
            Reset
          </button>
        )}
        {dirty && <span className="text-xs text-white/45">Unsaved changes</span>}
      </div>

      {confirm && (
        <Modal title="Enable this send?" onClose={() => setConfirm(null)} disabled={saving}>
          <p className="mt-3 text-sm text-[#ffae5b]">
            This arms <span className="font-medium text-white">{campaign.label}</span> to send at{' '}
            <span className="font-medium text-white">{draft.scheduled ? previewEt(draft.scheduled) : 'the set time'}</span>.
          </p>
          <p className="mt-3 text-sm text-white/85">
            {confirm.count === null
              ? 'Counting recipients…'
              : confirm.count < 0
                ? 'Recipient count unavailable — the send will reach all eligible, opted-in users.'
                : `It will email ${confirm.count.toLocaleString()} active, opted-in ${
                    confirm.count === 1 ? 'user' : 'users'
                  }.`}
          </p>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirm(null)}
              disabled={saving}
              className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:opacity-50 sm:min-w-[120px]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void persist(buildUpdate(draft, campaign))}
              disabled={saving || confirm.count === null}
              className="rounded-full bg-white/90 px-5 py-2 text-sm font-medium text-[#2a2237] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[160px]"
            >
              {saving ? 'Saving…' : 'Enable send'}
            </button>
          </div>
        </Modal>
      )}

      {resetOpen && (
        <Modal title="Reset this campaign?" onClose={() => setResetOpen(false)} disabled={saving}>
          <p className="mt-3 text-sm text-[#ffae5b]">
            This clears the send state and the per-recipient ledger for{' '}
            <span className="font-medium text-white">{campaign.label}</span>, and disables it. After reset you can
            re-arm it; a real send will then reach everyone eligible again.
          </p>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setResetOpen(false)}
              disabled={saving}
              className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:opacity-50 sm:min-w-[120px]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onReset()}
              disabled={saving}
              className="rounded-full bg-white/90 px-5 py-2 text-sm font-medium text-[#2a2237] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[140px]"
            >
              {saving ? 'Resetting…' : 'Reset'}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
